import { useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { teamMembers } from './TeamPage';
import { CTA, Footer } from './App';
import './MemberPage.css';

const Icons = {
  ArrowLeft: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5"/><path d="m12 19-7-7 7-7"/>
    </svg>
  ),
  Mail: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  ),
  External: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    </svg>
  ),
};

const getInitials = (name) =>
  name.split(' ').map((p) => p[0]?.toUpperCase() ?? '').slice(0, 2).join('');

// Brand mentions inside bios/highlights auto-link to their live sites.
// Order longest-first so multi-word names win the regex alternation.
const BRAND_LINKS = [
  { match: 'Mystic Media Film', href: 'https://mysticmediafilm.com' },
  { match: 'Creator Terminal', href: 'https://creatorterminal.com' },
  { match: 'MyShowBro', href: 'https://myshowbro.com' },
];

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const BRAND_REGEX = new RegExp(`(${BRAND_LINKS.map((b) => escapeRegExp(b.match)).join('|')})`, 'gi');
const brandFor = (text) => BRAND_LINKS.find((b) => b.match.toLowerCase() === text.toLowerCase());

// Split a string into plain text + inline brand links. Only the first mention
// of each brand within a string is linked, so a repeated name (e.g. "MyShowBro
// ... MyShowBro") doesn't become a wall of links.
const renderWithLinks = (text) => {
  const nodes = [];
  const linked = new Set();
  let cursor = 0;
  let key = 0;
  for (const match of text.matchAll(BRAND_REGEX)) {
    const matched = match[0];
    const start = match.index ?? 0;
    const brand = brandFor(matched);
    if (!brand || linked.has(brand.match)) continue;
    linked.add(brand.match);
    if (start > cursor) nodes.push(text.slice(cursor, start));
    nodes.push(
      <a key={`brand-${key++}`} className="member-link" href={brand.href} target="_blank" rel="noopener noreferrer">{matched}</a>
    );
    cursor = start + matched.length;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes.length ? nodes : text;
};

function MemberPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const member = teamMembers.find((m) => m.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (!member) {
    return <Navigate to="/team" replace />;
  }

  // Smart back: return to the previous page if there is one, else /team.
  const handleBack = (e) => {
    if (window.history.length > 1) {
      e.preventDefault();
      navigate(-1);
    }
  };

  // Build contact pills from whichever fields are present.
  const socials = [];
  if (member.email) socials.push({ label: member.email, href: `mailto:${member.email}`, icon: Icons.Mail });
  if (member.linkedin) socials.push({ label: 'LinkedIn', href: member.linkedin, icon: Icons.External });
  if (member.instagram) socials.push({ label: 'Instagram', href: member.instagram, icon: Icons.External });
  if (member.youtube) socials.push({ label: 'YouTube', href: member.youtube, icon: Icons.External });
  if (member.discord) socials.push({ label: 'Discord', href: member.discord, icon: Icons.External });
  if (member.website) socials.push({ label: 'Website', href: member.website, icon: Icons.External });

  const bioParagraphs = Array.isArray(member.bio) ? member.bio : (member.bio ? [member.bio] : []);
  const memberUrl = `https://www.missioncriticalsupportgroup.com/team/${member.slug}`;
  const shareImage = member.cover || member.photo;
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: member.name,
    jobTitle: member.role,
    url: memberUrl,
    ...(member.photo ? { image: `https://www.missioncriticalsupportgroup.com${member.photo}` } : {}),
    ...(member.tagline ? { description: member.tagline } : {}),
    ...(member.email ? { email: member.email } : {}),
    worksFor: { '@type': 'Organization', name: 'Mission Critical Support Group' },
  };

  return (
    <div className="member-page">
      <Helmet>
        <title>{member.name} - {member.role} | Mission Critical Support Group</title>
        <meta name="description" content={member.tagline || `${member.name}, ${member.role} at Mission Critical Support Group.`} />
        <link rel="canonical" href={memberUrl} />

        <meta property="og:type" content="profile" />
        <meta property="og:url" content={memberUrl} />
        <meta property="og:title" content={`${member.name} - ${member.role} | Mission Critical Support Group`} />
        <meta property="og:description" content={member.tagline || member.role} />
        <meta property="og:site_name" content="Mission Critical Support Group" />
        {shareImage && <meta property="og:image" content={`https://www.missioncriticalsupportgroup.com${shareImage}`} />}
        {shareImage && <meta property="og:image:alt" content={`${member.name} — ${member.role} at Mission Critical Support Group`} />}

        <meta name="twitter:card" content={shareImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={`${member.name} - ${member.role}`} />
        <meta name="twitter:description" content={member.tagline || member.role} />
        {shareImage && <meta name="twitter:image" content={`https://www.missioncriticalsupportgroup.com${shareImage}`} />}

        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
      </Helmet>

      {/* Nav */}
      <nav className="member-nav">
        <div className="member-nav-inner">
          <a href="/team" className="member-nav-back" onClick={handleBack}><Icons.ArrowLeft /><span>Back to Team</span></a>
        </div>
      </nav>

      {/* Hero */}
      <section
        className="member-hero"
        style={member.cover ? {
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.82), rgba(20, 32, 59, 0.88)), url(${member.cover})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        } : undefined}
      >
        <div className="member-hero-inner">
          <a href="/team" className="member-back" onClick={handleBack}><Icons.ArrowLeft /> Back to team</a>

          <div className="member-hero-grid">
            {member.photo
              ? <div className="member-photo" style={{ backgroundImage: `url(${member.photo})` }} />
              : <div className="member-photo member-photo-empty"><span className="member-photo-initials">{getInitials(member.name)}</span></div>}

            <div className="member-hero-info">
              <span className="member-role">{member.role}</span>
              <h1 className="member-name">{member.name}</h1>
              {member.tagline && <p className="member-tagline">{member.tagline}</p>}

              {socials.length > 0 && (
                <div className="member-socials">
                  {socials.map((s) => (
                    <a
                      key={s.label}
                      className="member-social"
                      href={s.href}
                      target={s.href.startsWith('mailto:') ? undefined : '_blank'}
                      rel={s.href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                    >
                      <s.icon /> {s.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bio */}
      <section className="member-bio">
        <div className="member-bio-inner">
          {bioParagraphs.length > 0
            ? bioParagraphs.map((paragraph, i) => <p key={i} className="member-bio-p">{renderWithLinks(paragraph)}</p>)
            : <p className="member-bio-empty">Full biography coming soon.</p>}

          {member.highlights && member.highlights.length > 0 && (
            <div className="member-highlights">
              <div className="member-highlights-label"><span className="line" />Highlights</div>
              {member.highlights.map((h, i) => (
                <div key={i} className="member-highlight">
                  <span className="member-highlight-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="member-highlight-text">{renderWithLinks(h)}</span>
                </div>
              ))}
            </div>
          )}

          <a href="/team" className="member-bio-back" onClick={handleBack}><Icons.ArrowLeft /> Back to team</a>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
}

export default MemberPage;
